// src/api.js
import axios from "axios";

// -----------------------------------------
// AUTO-DETECT ENVIRONMENT
// -----------------------------------------
const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

const PROD_API = "https://api.thecollectorstop.com";

export const API_BASE = isLocalHost
  ? (process.env.REACT_APP_API_URL || "http://localhost:8080")
  : PROD_API;

// AI base (falls back to API_BASE so /process_item proxy still works locally)
export const AI_BASE =
  (process.env.REACT_APP_AI_URL || "").replace(/\/+$/, "") ||
  API_BASE.replace(/\/+$/, "");

axios.defaults.withCredentials = true;

// -----------------------------------------
// AUTH STORAGE HELPERS
// -----------------------------------------
const LS = window.localStorage;
const SS = window.sessionStorage;
const AUTH_KEYS = { user: "user", currentUserName: "currentUserName" };

export function getAuth() {
  const userStr = LS.getItem(AUTH_KEYS.user) || SS.getItem(AUTH_KEYS.user);
  let user = null;
  try { user = userStr ? JSON.parse(userStr) : null; } catch {}
  return { user };
}

export function setAuth({ user, remember = false }) {
  const primary = remember ? LS : SS;
  const secondary = remember ? SS : LS;

  secondary.removeItem(AUTH_KEYS.user);
  secondary.removeItem(AUTH_KEYS.currentUserName);

  if (user) {
    primary.setItem(AUTH_KEYS.user, JSON.stringify(user));
    primary.setItem(AUTH_KEYS.currentUserName, user?.name || user?.username || "");
  } else {
    primary.removeItem(AUTH_KEYS.user);
    primary.removeItem(AUTH_KEYS.currentUserName);
  }

  window.dispatchEvent(new Event("storage"));
}

export function clearAuth() {
  [LS, SS].forEach(store => {
    store.removeItem(AUTH_KEYS.user);
    store.removeItem(AUTH_KEYS.currentUserName);
    store.removeItem("token");
  });
  window.dispatchEvent(new Event("storage"));
}

export function getToken() {
  return "";
}

// -----------------------------------------
// AXIOS INSTANCE
// -----------------------------------------
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;
  if (isFormData) {
    delete config.headers?.["Content-Type"];
    delete config.headers?.["content-type"];
    return config;
  }

  const hasBody = config.data !== undefined && config.data !== null;
  const isJsonLike =
    typeof config.data === "string" ||
    (typeof config.data === "object" && !Array.isArray(config.data));
  if (hasBody && isJsonLike && !config.headers?.["Content-Type"] && !config.headers?.["content-type"]) {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

// IMPORTANT:
// Do NOT auto-clear auth on 401.
// Session expiration or forced logout should be handled explicitly in UI logic.
// Automatically wiping storage here causes login loops.
api.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err)
);

// -----------------------------------------
// API ENDPOINT HELPERS
// -----------------------------------------
export async function getSiteStats() {
  const { data } = await api.get("/api/site-stats");
  return data;
}

export async function searchItems(q) {
  const { data } = await api.get("/api/search", { params: { q } });
  return data?.results || [];
}

export async function getCollection(username) {
  const { data } = await api.get(`/api/mycollection/${encodeURIComponent(username)}`);
  return data;
}

export async function getCollectionBySubcat(username, subcategoryId) {
  const { data } = await api.get(
    `/api/mycollection/${encodeURIComponent(username)}/subcategory/${subcategoryId}`
  );
  return data;
}

export async function loginApi({ username, password }) {
  const { data } = await api.post("/api/login", { username, password });
  setAuth({ user: data.user, remember: true });
  return data;
}

export async function registerApi(payload) {
  const { data } = await api.post("/api/register", payload);
  return data;
}

export async function addToCollection(body) {
  const { data } = await api.post("/api/add-to-collection", body);
  return data;
}

export async function parseCollectionImport(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/api/mycollection/import/parse", form);
  return data;
}

export async function matchCollectionImport(payload) {
  const { data } = await api.post("/api/mycollection/import/match", payload);
  return data;
}

export async function commitCollectionImport(payload) {
  const { data } = await api.post("/api/mycollection/import/commit", payload);
  return data;
}

export async function createCollectionImportSession(payload) {
  const { data } = await api.post("/api/mycollection/import/sessions", payload);
  return data;
}

export async function listCollectionImportSessions() {
  const { data } = await api.get("/api/mycollection/import/sessions");
  return data;
}

export async function deleteCollectionImportSession(sessionId) {
  const { data } = await api.delete(`/api/mycollection/import/sessions/${encodeURIComponent(sessionId)}`);
  return data;
}

export async function getCollectionImportSession(sessionId) {
  const { data } = await api.get(`/api/mycollection/import/sessions/${encodeURIComponent(sessionId)}`);
  return data;
}

export async function investigateCollectionImportSession(sessionId) {
  const { data } = await api.post(`/api/mycollection/import/sessions/${encodeURIComponent(sessionId)}/investigate`);
  return data;
}

export async function reviewCollectionImportRow(sessionId, rowId, payload) {
  const { data } = await api.patch(`/api/mycollection/import/sessions/${encodeURIComponent(sessionId)}/rows/${encodeURIComponent(rowId)}/review`, payload);
  return data;
}

export async function searchCollectionImportCatalog(params) {
  const { data } = await api.get("/api/mycollection/import/catalog-search", { params });
  return data?.results || [];
}

export async function finalizeCollectionImportSession(sessionId, payload) {
  const { data } = await api.post(`/api/mycollection/import/sessions/${encodeURIComponent(sessionId)}/finalize`, payload);
  return data;
}

export async function manualAdd(body) {
  const { data } = await api.post("/api/manual-add", body);
  return data;
}

export async function submitFeedback(body) {
  const { data } = await api.post("/api/feedback", body);
  return data;
}
